import { useQueryClient } from '@tanstack/react-query';
import { useGetETSTicketsQuery } from 'gcs-common/clients/api/entities/tickets/tickets-api';
import {
  convertETSTicketsFiltersToQuery,
  type ETSTicketsFilters,
  getGetETSTicketsQueryKey,
  INITIAL_ETS_TICKETS_DASHBOARD_PAGINATION_QUERY_PARAMS,
  useETSTicketsQueryParams,
  useSelectedETSTicketIdQueryParams,
} from 'gcs-common/clients/api/entities/tickets/tickets-api-helpers';
import {
  useGetETSTicketNotificationsCountSelector,
  useGetETSTicketNotificationsSelector,
} from 'gcs-common/clients/api/entities/tickets/tickets-api-selectors';
import TicketStatus from 'gcs-common/components/Tickets/TicketStatus/TicketStatus';
import { Badge } from 'gcs-common/design-system/ui/badge';
import USER_TYPE from 'gcs-common/helper/userTypeHelper';
import { TicketDashboardTabNames } from 'gcs-common/slices/tickets/ticketsConstants';
import type { Response } from 'gcs-common/types/typeHelper';
import moment from 'moment';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo } from 'react';
import type { Column } from 'react-table';
import PaginatedTable from '../../../PaginatedTable/PaginatedTable';
import styles from './styles.module.scss';
import TicketDashboardNotification from './TicketDashboardNotification/TicketDashboardNotification';

interface TicketsDashboardTableProps {
  setShowDetails: Dispatch<SetStateAction<boolean>>;
}

const TicketDashboardTable = (props: TicketsDashboardTableProps) => {
  const { setShowDetails } = props;
  const queryClient = useQueryClient();
  const { etsTicketsfilters: filters, setETSTicketsQueryParams } = useETSTicketsQueryParams();
  const ticketNotifications = useGetETSTicketNotificationsSelector();
  const ticketNotificationsCount = useGetETSTicketNotificationsCountSelector();
  const { setSelectedTicketId } = useSelectedETSTicketIdQueryParams();
  const { sortedColumnId, sortOrder } = filters;

  useEffect(() => {
    if ((!filters.pageIndex && filters.pageIndex !== 0) || !filters.pageSize) {
      setETSTicketsQueryParams(INITIAL_ETS_TICKETS_DASHBOARD_PAGINATION_QUERY_PARAMS, false);
    }
  }, []);

  const setSortedColumnId = (newColumnId: ETSTicketsFilters['sortedColumnId']) => {
    setETSTicketsQueryParams({
      sortedColumnId: newColumnId ?? undefined,
    });
  };
  const setSortOrder = (newSortOrder: ETSTicketsFilters['sortOrder']) => {
    setETSTicketsQueryParams({
      sortOrder: newSortOrder ? newSortOrder.toString() : undefined,
    });
  };

  const columns: Column<Response<'/chat/ets/tickets/{ticket_id}', 'get'>>[] = useMemo(
    () => [
      {
        Header: 'Ticket-ID',
        accessor: 'externalJiraKey',
        disableSortBy: true,
        Cell: (cell) => {
          const {
            value,
            row: { original },
          } = cell;
          const hasNotification = original.id in ticketNotifications || original.hasNotifications;

          return (
            <div className={styles.idAndNotification}>
              {hasNotification && <TicketDashboardNotification />}
              {value}
            </div>
          );
        },
        styles: { width: '10%' },
      },
      {
        Header: 'Kommission / Beschreibung',
        accessor: 'title',
        disableSortBy: true,
        Cell: ({ row, value }) => {
          return (
            <div className="flex flex-col gap-1">
              {row.original.customerOrderText && (
                <div className={`${styles.commission} ${styles.ellipsis}`}>{row.original.customerOrderText}</div>
              )}
              <div className={styles.ellipsis}>{value}</div>
            </div>
          );
        },
        styles: { width: '20%' },
      },
      {
        Header: 'Kundenname',
        accessor: 'customerName',
        disableSortBy: true,
        styles: { width: '12%' },
      },
      {
        Header: 'Kunden-Nr.',
        accessor: 'customerNumber',
        disableSortBy: true,
        styles: { width: '12%' },
      },
      {
        Header: 'Ersteller',
        accessor: 'creatorName',
        disableSortBy: true,
        styles: { width: '12%' },
        Cell: ({ row }) => {
          return (
            <div className="flex flex-col gap-2">
              <div>{row.original.creatorName}</div>
              {row.original.creatorUserType === USER_TYPE.CRAFTSMAN ? <Badge variant="soft_accent">Kunde</Badge> : null}
            </div>
          );
        },
      },
      {
        Header: 'Erstelldatum',
        accessor: 'createdAt',
        disableSortBy: true,
        Cell: (cell) => {
          const { value } = cell;
          const formattedDate = moment(value).format('DD.MM.YYYY');
          return <div>{formattedDate}</div>;
        },
        styles: { width: '10%' },
      },
      {
        Header: 'Letztes Update',
        accessor: 'jiraLastSyncedAt',
        disableSortBy: true,
        Cell: (cell) => {
          const { value } = cell;
          const formattedDate = moment(value).format('DD.MM.YYYY, HH:mm');
          return <div>{formattedDate}</div>;
        },
        styles: { width: '12%' },
      },
      {
        Header: 'Status',
        accessor: 'status',
        disableSortBy: true,
        Cell: ({ row }) => {
          if (row.original.missingKbnInGrid) {
            return (
              <div className="flex flex-col gap-2">
                <TicketStatus status={row.original.status} />
                <TicketStatus status={TicketDashboardTabNames.NOT_LISTED} />
              </div>
            );
          }
          return <TicketStatus status={row.original.status} />;
        },
        styles: { width: '12%' },
      },
    ],
    [ticketNotifications],
  );

  const getRowProps = (row: { original: { id: string; hasNotifications: boolean } }) => {
    const hasNotification = row.original.id in ticketNotifications || row.original.hasNotifications;

    return {
      className: hasNotification && styles.rowWithNotification,
    };
  };

  const handlePagination = useCallback((newPageIndex: number, newPageSize: number) => {
    setETSTicketsQueryParams({
      pageIndex: newPageIndex
        ? newPageIndex.toString()
        : INITIAL_ETS_TICKETS_DASHBOARD_PAGINATION_QUERY_PARAMS.pageIndex,
      pageSize: newPageSize ? newPageSize.toString() : INITIAL_ETS_TICKETS_DASHBOARD_PAGINATION_QUERY_PARAMS.pageSize,
    });
  }, []);

  const { data: ticketsData, isFetching: dataIsLoading } = useGetETSTicketsQuery({
    query: convertETSTicketsFiltersToQuery(filters),
  });

  const data = ticketsData ? ticketsData.data : undefined;
  const pagination = ticketsData ? ticketsData.pagination : undefined;

  useEffect(() => {
    queryClient
      .invalidateQueries({
        queryKey: getGetETSTicketsQueryKey(convertETSTicketsFiltersToQuery(filters)),
      })
      .then(() => {})
      .catch(() => {});
  }, [ticketNotificationsCount]);

  const onTicketSelected = (ticket: Response<'/chat/ets/tickets/{ticket_id}', 'get'>) => {
    setSelectedTicketId(ticket.id);
    setShowDetails(true);
  };

  return (
    <div className={styles.dashboardTable}>
      {data && (
        <PaginatedTable
          onRowSelected={onTicketSelected}
          data={data}
          // @ts-expect-error TODO Migrate PaginatedTable to ts
          columns={columns}
          // @ts-expect-error TODO Migrate PaginatedTable to ts
          getRowProps={getRowProps}
          pagination={pagination}
          initialPageIndex={filters.pageIndex}
          initialPageSize={filters.pageSize}
          useQueryPagination={true}
          useNewStyles
          onPaginationChanged={handlePagination}
          sortIsLoading={dataIsLoading}
          // @ts-expect-error TODO Migrate PaginatedTable to ts
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          // @ts-expect-error TODO Migrate PaginatedTable to ts
          sortedColumnId={sortedColumnId}
          setSortedColumnId={setSortedColumnId}
        />
      )}
    </div>
  );
};

export default TicketDashboardTable;
